import { onBeforeRouteLeave } from 'vue-router';
import { ref, Ref, watch } from 'vue';

export const useFormLeaveConfirmation = <T extends Record<string, any>>(
  formData: Ref<T> | null = null,
  initialData: Ref<T | null> = ref(null)
): {
  isFormChanged: Ref<boolean>;
  setupFormLeaveConfirmation: () => () => void;
  saveInitialData: () => void;
  resetFormChanged: () => void;
} => {
  const MESSAGE =
    'このページを離れますか？\n入力内容が保存されていません。本当に移動してもよろしいですか？';
  const isFormChanged = ref(false);
  let shouldConfirm = true;
  let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;
  let pagehideHandler: ((e: PageTransitionEvent) => void) | null = null;
  let cleanupBeforeUnload: (() => void) | null = null;

  // 2ステップ目以降のページかどうかを判定
  const isMultiStepPage = (): boolean => {
    if (typeof window === 'undefined') return false;

    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    // 完了画面は除外
    const completedPages = [
      '/signup/completed',
      '/signup/register_complete',
      '/candidate/completed',
      '/candidate/register_complete',
      '/candidate/reset_password/completed',
      '/candidate/forgot_password_complete',
    ];

    if (completedPages.some(page => pathname === page)) {
      return false;
    }

    // /signup/new の2ステップ目以降のみ
    if (pathname === '/signup/new') {
      const isMultiStep = searchParams.has('step_two') || searchParams.has('step_three');
      return isMultiStep;
    }

    // その他のページは通常の動作
    return false;
  };

  // フォーム変更を監視（安全な実装）
  if (formData) {
    watch(
      formData,
      newData => {
        try {
          // 安全なチェックを追加
          if (initialData && initialData.value && newData) {
            isFormChanged.value = JSON.stringify(newData) !== JSON.stringify(initialData.value);
          } else {
            isFormChanged.value = false;
          }
        } catch (error) {
          isFormChanged.value = false;
        }
      },
      { deep: true }
    );
  }

  const saveInitialData = (): void => {
    try {
      if (formData && formData.value && initialData) {
        initialData.value = JSON.parse(JSON.stringify(formData.value));
      }
    } catch (error) {
      if (initialData) {
        initialData.value = null;
      }
    }
  };

  const resetFormChanged = (): void => {
    isFormChanged.value = false;
  };

  const setupFormLeaveConfirmation = (): (() => void) => {
    try {
      // 2ステップ目以降のページの場合は、初期状態からbeforeunloadを有効にする
      const isMultiStep = isMultiStepPage();

      if (isMultiStep) {
        isFormChanged.value = true;
      } else {
      }

      // 既存のイベントリスナーをクリーンアップ
      if (cleanupBeforeUnload) {
        cleanupBeforeUnload();
      }

      // beforeunloadイベントハンドラーを作成
      beforeUnloadHandler = (e: BeforeUnloadEvent): void => {
        if (!shouldConfirm || !isFormChanged.value) {
          return;
        }
        e.preventDefault();
        e.returnValue = MESSAGE;

        const message = MESSAGE;
        e.returnValue = message;
      };

      // beforeunloadイベントリスナーを追加
      if (beforeUnloadHandler) {
        window.addEventListener('beforeunload', beforeUnloadHandler);
      }

      const originalOnBeforeUnload = window.onbeforeunload;
      window.onbeforeunload = function (e: BeforeUnloadEvent): string | void {
        if (!shouldConfirm || !isFormChanged.value) {
          return undefined;
        }

        return MESSAGE;
      };

      // pagehideイベントハンドラーを作成
      pagehideHandler = (e: PageTransitionEvent): void => {
        if (!shouldConfirm || !isFormChanged.value) {
          return;
        }

        // pagehideイベントでは確認ダイアログを表示できないため、ログのみ出力
      };

      // pagehideイベントリスナーを追加
      if (pagehideHandler) {
        window.addEventListener('pagehide', pagehideHandler);
      }

      // Vue RouterのbeforeRouteLeaveを設定
      try {
        // Vue Routerのコンテキスト内でのみ実行
        if (typeof window !== 'undefined' && window.location.pathname.includes('/candidate/')) {
          onBeforeRouteLeave((_to, _from, next) => {
            if (!isFormChanged.value) {
              next();
              return;
            }
            const answer = window.confirm(MESSAGE);
            if (answer) {
              shouldConfirm = false;
              next();
            }
          });
        } else {
        }
      } catch (routeError) {
        console.error('[useFormLeaveConfirmation] onBeforeRouteLeave設定エラー:', routeError);
        // エラーが発生しても処理を継続
      }

      // クリーンアップ関数を保存
      cleanupBeforeUnload = (): void => {
        try {
          if (beforeUnloadHandler) {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
            beforeUnloadHandler = null;
          } else {
          }
          if (pagehideHandler) {
            window.removeEventListener('pagehide', pagehideHandler);
            pagehideHandler = null;
          } else {
          }

          // window.onbeforeunloadを確実にリセット
          window.onbeforeunload = null;

          // フォーム変更状態もリセット
          isFormChanged.value = false;
        } catch (cleanupError) {
          console.error('[useFormLeaveConfirmation] クリーンアップ実行エラー:', cleanupError);
        }
      };

      return cleanupBeforeUnload;
    } catch (error) {
      return () => {}; // 空のクリーンアップ関数を返す
    }
  };

  return {
    isFormChanged,
    setupFormLeaveConfirmation,
    saveInitialData,
    resetFormChanged,
  };
};
